import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Wrench, AlertCircle } from 'lucide-react';

export default function HouseMaintenanceTab({ house, houseId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, [houseId]);

  const fetchRequests = async () => {
    try {
      const response = await api.get(`/maintenance`, { params: { houseId } });
      setRequests(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load maintenance requests');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (requestId) => {
    if (!confirm('Mark this maintenance request as resolved?')) return;
    try {
      await api.post(`/maintenance/${requestId}/resolve`);
      toast.success('Request marked as resolved');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resolve request');
    }
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      LOW: 'secondary',
      MEDIUM: 'default',
      HIGH: 'warning',
      URGENT: 'destructive'
    };
    return <Badge variant={variants[priority] || 'default'}>{priority}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      OPEN: 'destructive',
      IN_PROGRESS: 'warning',
      RESOLVED: 'default'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-4">
      {requests.map((request) => (
        <Card key={request.id}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium">{request.title}</h4>
                  {getPriorityBadge(request.priority)}
                  {getStatusBadge(request.status)}
                </div>
                {request.description && <p className="text-sm text-muted-foreground">{request.description}</p>}
                <p className="text-xs text-muted-foreground">
                  Reported by {request.reported_by_name} on {new Date(request.created_at).toLocaleDateString()}
                </p>
              </div>
              {request.status !== 'RESOLVED' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleResolve(request.id)}
                >
                  Resolve
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {requests.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Wrench className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No maintenance requests for this house</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
